import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import Footer from "@/components/landing/Footer";
import Breadcrumbs from "@/components/landing/Breadcrumbs";
import Seo from "@/components/Seo";

const Section = ({ number, title, children }) => (
  <section className="border-b border-[hsl(var(--border))] py-10 last:border-none">
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <div className="lg:col-span-3">
        <p className="font-display text-3xl text-[hsl(var(--sand-dark))]">
          {number}
        </p>
        <h2 className="mt-1 font-display text-2xl text-[hsl(var(--ink))]">
          {title}
        </h2>
      </div>
      <div className="prose prose-neutral max-w-none text-[hsl(var(--ink-soft))] lg:col-span-9">
        {children}
      </div>
    </div>
  </section>
);

const List = ({ items }) => (
  <ul className="mt-3 list-disc space-y-1.5 pl-5 marker:text-[hsl(var(--sand-dark))]">
    {items.map((i) => (
      <li key={i}>{i}</li>
    ))}
  </ul>
);

export const Privacy = () => {
  return (
    <main data-testid="privacy-page" className="bg-background">
      <Seo
        title="Privacyverklaring | EquiSight"
        description="Welke persoonsgegevens EquiSight verwerkt, hoe wij ze beveiligen en welke rechten je hebt."
        path="/privacy"
        breadcrumbs={[{ name: "Privacyverklaring", path: "/privacy" }]}
      />
      <AnnouncementBar />
      <Header />
      <Breadcrumbs items={[{ name: "Privacyverklaring", path: "/privacy" }]} />

      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
            Juridisch
          </p>
          <h1 className="mt-4 font-display text-5xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] sm:text-6xl">
            Privacyverklaring
          </h1>
          <p className="mt-6 text-[hsl(var(--ink-soft))]">
            Laatst bijgewerkt: 11-05-2026
          </p>
          <p className="mt-4 max-w-3xl text-[hsl(var(--ink-soft))]">
            Welkom bij EquiSight. Wij hechten grote waarde aan de bescherming
            van jouw persoonsgegevens en zorgen ervoor dat jouw privacy wordt
            gerespecteerd. In deze privacyverklaring leggen wij uit welke
            gegevens wij verzamelen, waarom wij dit doen en hoe wij hiermee
            omgaan.
          </p>
          <p className="mt-6">
            <Link
              to="/"
              className="text-sm text-[hsl(var(--sand-dark))] underline-offset-4 hover:underline"
            >
              ← Terug naar home
            </Link>
          </p>
        </div>
      </section>

      <article className="bg-background pb-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Section number="1" title="Wie zijn wij?">
            <p>
              EquiSight is een applicatie gericht op het beheren, analyseren en
              verbeteren van de gezondheid, training en prestaties van paarden
              en ruiters.
            </p>
            <p className="mt-4">
              <strong className="text-[hsl(var(--ink))]">
                Verantwoordelijke voor gegevensverwerking:
              </strong>
              <br />
              EquiSight
              <br />
              SaFleu Equestrian Centre BV
              <br />
              support@equisight.nl
              <br />
              Kerklaan 7B, 4944 VA Raamsdonk
              <br />
              Kamer van Koophandel: 97318159
            </p>
          </Section>

          <Section number="2" title="Welke gegevens verzamelen wij?">
            <p>Wij kunnen de volgende persoonsgegevens verzamelen:</p>

            <p className="mt-5 font-medium text-[hsl(var(--ink))]">Accountgegevens</p>
            <List items={[
              "Naam",
              "E-mailadres",
              "Telefoonnummer (optioneel)",
              "Inloggegevens",
            ]} />

            <p className="mt-5 font-medium text-[hsl(var(--ink))]">Gebruikersgegevens</p>
            <List items={[
              "Voorkeuren en instellingen",
              "Abonnementstype (gratis/premium)",
              "Gebruik van functionaliteiten binnen de app",
            ]} />

            <p className="mt-5 font-medium text-[hsl(var(--ink))]">Paard- en sportgegevens</p>
            <List items={[
              "Naam, leeftijd, ras en kenmerken van paarden",
              "Trainingsgegevens",
              "Wedstrijdresultaten en analyses",
              "Gezondheidsinformatie (zoals hoefsmid, tandarts, fysio rapportages)",
              "Foto's en video's",
            ]} />

            <p className="mt-5 font-medium text-[hsl(var(--ink))]">Locatiegegevens</p>
            <List items={[
              "Indien ingeschakeld: locatie voor functies zoals routeberekening (bijv. hoefsmid ETA)",
            ]} />

            <p className="mt-5 font-medium text-[hsl(var(--ink))]">Communicatie</p>
            <List items={["Berichten, notities en feedback"]} />
          </Section>

          <Section number="3" title="Waarom verzamelen wij deze gegevens?">
            <p>Wij verwerken persoonsgegevens voor de volgende doeleinden:</p>
            <List items={[
              "Het leveren en verbeteren van de EquiSight app",
              "Het aanmaken en beheren van gebruikersaccounts",
              "Het bieden van gepersonaliseerde inzichten en analyses",
              "Het faciliteren van samenwerking (bijv. met trainers, hoefsmid, dierenarts)",
              "Het versturen van notificaties (zoals afspraken en alerts)",
              "Klantenservice en support",
              "Wettelijke verplichtingen",
            ]} />
          </Section>

          <Section number="4" title="Rechtsgrond voor verwerking">
            <p>Wij verwerken jouw gegevens op basis van:</p>
            <List items={[
              "Uitvoering van een overeenkomst (gebruik van de app)",
              "Toestemming (bijv. voor locatie of notificaties)",
              "Gerechtvaardigd belang (verbetering van onze diensten)",
              "Wettelijke verplichtingen",
            ]} />
          </Section>

          <Section number="5" title="Delen van gegevens">
            <p>Wij delen jouw gegevens alleen wanneer noodzakelijk:</p>
            <List items={[
              "Met dienstverleners (hosting, analytics, notificaties)",
              "Met andere gebruikers indien jij toegang verleent (bijv. trainer of eigenaar)",
              "Met bevoegde autoriteiten indien wettelijk verplicht",
            ]} />
            <p className="mt-4 font-medium text-[hsl(var(--ink))]">
              Wij verkopen jouw gegevens nooit aan derden.
            </p>
          </Section>

          <Section number="6" title="Opslag en beveiliging">
            <p>
              Wij nemen passende technische en organisatorische maatregelen om
              jouw gegevens te beschermen, waaronder:
            </p>
            <List items={[
              "Versleutelde opslag (encryptie)",
              "Beveiligde servers",
              "Toegangscontrole",
              "Regelmatige updates en monitoring",
            ]} />
          </Section>

          <Section number="7" title="Bewaartermijn">
            <p>
              Wij bewaren jouw gegevens niet langer dan nodig is voor de
              doeleinden waarvoor ze zijn verzameld, tenzij een langere
              bewaartermijn wettelijk verplicht is.
            </p>
            <p className="mt-4">
              Bij verwijdering van jouw account worden jouw gegevens binnen een
              redelijke termijn verwijderd of geanonimiseerd.
            </p>
          </Section>

          <Section number="8" title="Jouw rechten">
            <p>Je hebt het recht om:</p>
            <List items={[
              "Je gegevens in te zien",
              "Je gegevens te corrigeren",
              "Je gegevens te laten verwijderen",
              "Bezwaar te maken tegen verwerking",
              "Gegevens over te dragen (dataportabiliteit)",
              "Toestemming in te trekken",
            ]} />
            <p className="mt-4">
              Neem hiervoor contact op via:{" "}
              <a
                href="mailto:support@equisight.nl"
                className="text-[hsl(var(--forest))] underline-offset-4 hover:underline"
              >
                support@equisight.nl
              </a>
            </p>
            <p className="mt-4">
              Daarnaast kun je een klacht indienen bij de{" "}
              <a
                href="https://autoriteitpersoonsgegevens.nl"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[hsl(var(--forest))] underline-offset-4 hover:underline"
              >
                Autoriteit Persoonsgegevens
              </a>
              .
            </p>
          </Section>

          <Section number="9" title="Cookies en tracking">
            <p>
              EquiSight maakt gebruik van cookies en vergelijkbare technologieën om:
            </p>
            <List items={[
              "De app goed te laten functioneren",
              "Gebruik te analyseren",
              "De gebruikerservaring te verbeteren",
            ]} />
            <p className="mt-4">Voor zover vereist vragen wij hiervoor toestemming.</p>
          </Section>

          <Section number="10" title="Derde partijen">
            <p>EquiSight kan gebruik maken van externe diensten, zoals:</p>
            <List items={[
              "Hostingproviders",
              "Analytics tools",
              "Push notificatie services",
            ]} />
            <p className="mt-4">
              Wij zorgen ervoor dat deze partijen voldoen aan de geldende
              privacywetgeving.
            </p>
          </Section>

          <Section number="11" title="Internationale doorgifte">
            <p>
              Indien gegevens buiten de Europese Economische Ruimte worden
              verwerkt, zorgen wij voor passende waarborgen conform de AVG.
            </p>
          </Section>

          <Section number="12" title="Wijzigingen">
            <p>
              Wij kunnen deze privacyverklaring aanpassen. De meest actuele
              versie is altijd beschikbaar op:
            </p>
            <p className="mt-3">
              <a
                href="https://www.equisight.nl/privacy"
                className="text-[hsl(var(--forest))] underline-offset-4 hover:underline"
              >
                www.equisight.nl/privacy
              </a>
            </p>
          </Section>

          <Section number="13" title="Contact">
            <p>Heb je vragen over deze privacyverklaring? Neem contact op via:</p>
            <p className="mt-4 font-display text-xl text-[hsl(var(--ink))]">
              📧{" "}
              <a
                href="mailto:support@equisight.nl"
                className="text-[hsl(var(--forest))] underline-offset-4 hover:underline"
              >
                support@equisight.nl
              </a>
            </p>
          </Section>
        </div>
      </article>

      <Footer />
    </main>
  );
};

export default Privacy;
